// src/utils/colorUtils.js
/**
 * @module colorUtils
 * Utility functions for assigning colors to tokens for visualization purposes.
 * Provides both palette-based and spectrum-based coloring strategies.
 */

/**
 * Predefined color palette for token-ID-based coloring.
 * Each color is a hex string. The palette is cycled through using modulo arithmetic,
 * so any token ID will always map to a color in this array.
 */
const tokenColorPalette = [
  "#E31A1C", // Red
  "#D95F02", // Orange
  "#FF7F00", // Bright Orange
  "#FDBF6F", // Light Orange
  "#FFFF99", // Yellow
  "#B2DF8A", // Light Green
  "#33A02C", // Green
  "#1B9E77", // Teal
  "#A6CEE3", // Light Blue
  "#1F78B4", // Blue
  "#7570B3", // Purple
  "#6A3D9A", // Dark Purple
  "#CAB2D6", // Lavender
  "#E7298A", // Pink
  "#FB9A99", // Light Red
  "#B15928", // Brown
];

/**
 * Returns a deterministic color for a token based on its ID.
 * This ensures that the same token ID always maps to the same color,
 * which is useful for visually grouping identical tokens.
 *
 * @param {number} id - Token identifier (should be a non-negative integer).
 * @returns {string} Hex color string from the palette.
 *
 * Example:
 *   getTokenColor(3); // "#D95F02"
 */
export function getTokenColor(id) {
  return tokenColorPalette[id % tokenColorPalette.length];
}

/**
 * Returns an HSL color along a blue-to-red spectrum based on the token's sequence index.
 * This is useful for visualizing the order of tokens, such as in a sentence or document.
 * The first token is blue (hue 240), the last is red (hue 0), and others are interpolated.
 *
 * @param {number} index - Zero-based token index in the sequence.
 * @param {number} total - Total number of tokens in the sequence.
 * @returns {string} HSL color string representing the token's position.
 *
 * Example:
 *   getSequenceColor(0, 5); // "hsl(240,100%,50%)" (blue)
 *   getSequenceColor(4, 5); // "hsl(0,100%,50%)" (red)
 */
export function getSequenceColor(index, total) {
  if (total <= 1) {
    // If only one token, fall back to the palette's first color
    return getTokenColor(0);
  }
  // Map index 0→blue (240°) to index total-1→red (0°)
  const hue = 240 - (240 * index) / (total - 1);
  return `hsl(${hue},100%,75%)`;
}
